// ==================== CUSTOM FIELDS MANAGEMENT PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Settings2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  GripVertical,
  Type,
  Hash,
  Calendar,
  Mail,
  Phone,
  Link,
  List,
  ListChecks,
  ToggleLeft,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import { Button, Card, Badge, Input, Modal, Select, Checkbox, ConfirmDialog } from '../../../components/ui';
import type { SelectOption } from '../../../components/ui/Select';
import { contactsService } from '../../../services/contacts.service';
import { useOrganizations } from '../../../hooks/useOrganizations';
import type { ContactCustomField } from '../../../types';

// ==================== FIELD TYPE OPTIONS ====================

const FIELD_TYPES: SelectOption[] = [
  { value: 'TEXT', label: 'Text', icon: <Type className="w-4 h-4" /> },
  { value: 'NUMBER', label: 'Number', icon: <Hash className="w-4 h-4" /> },
  { value: 'DATE', label: 'Date', icon: <Calendar className="w-4 h-4" /> },
  { value: 'EMAIL', label: 'Email', icon: <Mail className="w-4 h-4" /> },
  { value: 'PHONE', label: 'Phone', icon: <Phone className="w-4 h-4" /> },
  { value: 'URL', label: 'URL', icon: <Link className="w-4 h-4" /> },
  { value: 'SELECT', label: 'Dropdown (Single)', icon: <List className="w-4 h-4" /> },
  { value: 'MULTI_SELECT', label: 'Dropdown (Multiple)', icon: <ListChecks className="w-4 h-4" /> },
  { value: 'BOOLEAN', label: 'Yes/No Toggle', icon: <ToggleLeft className="w-4 h-4" /> },
];

const getFieldTypeIcon = (type: string) => {
  const icons: Record<string, React.ReactNode> = {
    TEXT: <Type className="w-4 h-4" />,
    NUMBER: <Hash className="w-4 h-4" />,
    DATE: <Calendar className="w-4 h-4" />,
    EMAIL: <Mail className="w-4 h-4" />,
    PHONE: <Phone className="w-4 h-4" />,
    URL: <Link className="w-4 h-4" />,
    SELECT: <List className="w-4 h-4" />,
    MULTI_SELECT: <ListChecks className="w-4 h-4" />,
    BOOLEAN: <ToggleLeft className="w-4 h-4" />,
  };
  return icons[type] || <Type className="w-4 h-4" />;
};

const getFieldTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    TEXT: 'Text',
    NUMBER: 'Number',
    DATE: 'Date',
    EMAIL: 'Email',
    PHONE: 'Phone',
    URL: 'URL',
    SELECT: 'Dropdown',
    MULTI_SELECT: 'Multi-Select',
    BOOLEAN: 'Yes/No',
  };
  return labels[type] || type;
};

// ==================== FORM STATE INTERFACE ====================

interface FieldFormData {
  name: string;
  label: string;
  type: string;
  isRequired: boolean;
  options: string[];
  sortOrder: number;
}

const initialFormData: FieldFormData = {
  name: '',
  label: '',
  type: 'TEXT',
  isRequired: false,
  options: [],
  sortOrder: 0,
};

// ==================== SORTABLE FIELD ITEM ====================

interface SortableFieldItemProps {
  field: ContactCustomField;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}

const SortableFieldItem = ({ field, index, onEdit, onDelete }: SortableFieldItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 transition-colors ${
        isDragging 
          ? 'bg-orange-500/10 shadow-lg shadow-orange-500/10 border-y border-orange-500/30' 
          : 'hover:bg-white/5'
      }`}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className={`p-1.5 rounded-md transition-colors cursor-grab active:cursor-grabbing ${
          isDragging 
            ? 'text-orange-400 bg-orange-500/20' 
            : 'text-gray-500 hover:text-orange-400 hover:bg-orange-500/10'
        }`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Position Badge */}
      <div className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center text-xs text-gray-500 font-mono shrink-0">
        {index + 1}
      </div>

      {/* Type Icon */}
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center text-orange-400 shrink-0">
        {getFieldTypeIcon(field.type)}
      </div>

      {/* Field Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-white truncate">{field.label}</h4>
          {field.isRequired && (
            <Badge variant="error" className="text-xs">Required</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-(--color-muted-gray)">
            Field: <span className="text-gray-400 font-mono">{field.name}</span>
          </span>
          <span className="text-xs text-(--color-muted-gray)">
            Type: <span className="text-gray-400">{getFieldTypeLabel(field.type)}</span>
          </span>
          {(field.type === 'SELECT' || field.type === 'MULTI_SELECT') && field.options && (
            <span className="text-xs text-(--color-muted-gray)">
              {field.options.length} option{field.options.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="ghost"
          onClick={onEdit}
          className="p-2"
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          onClick={onDelete}
          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const CustomFieldsPage = () => {
  // State
  const [fields, setFields] = useState<ContactCustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<ContactCustomField | null>(null);
  const [formData, setFormData] = useState<FieldFormData>(initialFormData);
  const [newOption, setNewOption] = useState('');

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<ContactCustomField | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ==================== FETCH FIELDS ====================

  // Multiple organizations: the account chosen in the header (all accounts when none)
  const { selectedOrganizationId, requireAccount } = useOrganizations();

  const fetchFields = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contactsService.getCustomFields(selectedOrganizationId);
      setFields(response || []);
    } catch (err) {
      console.error('Failed to fetch custom fields:', err);
      setError('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizationId]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  // ==================== HANDLERS ====================

  const handleOpenModal = (field?: ContactCustomField) => {
    if (field) {
      setEditingField(field);
      setFormData({
        name: field.name,
        label: field.label,
        type: field.type,
        isRequired: field.isRequired,
        options: field.options || [],
        sortOrder: field.sortOrder,
      });
    } else {
      // Multiple organizations: a new field is created in the account chosen in the header
      if (!requireAccount('add a custom field')) return;
      setEditingField(null);
      setFormData({
        ...initialFormData,
        sortOrder: fields.length,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingField(null);
    setFormData(initialFormData);
    setNewOption('');
  };

  const handleAddOption = () => {
    if (newOption.trim() && !formData.options.includes(newOption.trim())) {
      setFormData((prev) => ({
        ...prev,
        options: [...prev.options, newOption.trim()],
      }));
      setNewOption('');
    }
  };

  const handleRemoveOption = (optionToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((opt) => opt !== optionToRemove),
    }));
  };

  const handleSave = async () => {
    // Validate
    if (!formData.name.trim()) {
      setError('Field name is required');
      return;
    }
    if (!formData.label.trim()) {
      setError('Field label is required');
      return;
    }
    if ((formData.type === 'SELECT' || formData.type === 'MULTI_SELECT') && formData.options.length === 0) {
      setError('At least one option is required for dropdown fields');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        name: formData.name.trim().toLowerCase().replace(/\s+/g, '_'),
        label: formData.label.trim(),
        type: formData.type as 'TEXT' | 'NUMBER' | 'DATE' | 'EMAIL' | 'PHONE' | 'URL' | 'SELECT' | 'MULTI_SELECT' | 'BOOLEAN',
        isRequired: formData.isRequired,
        options: formData.type === 'SELECT' || formData.type === 'MULTI_SELECT' ? formData.options : undefined,
        sortOrder: formData.sortOrder,
      };

      if (editingField) {
        await contactsService.updateCustomField(editingField.id, payload);
        setSuccess('Custom field updated successfully');
      } else {
        await contactsService.createCustomField({
          ...payload,
          ...(selectedOrganizationId && { organizationId: selectedOrganizationId }),
        });
        setSuccess('Custom field created successfully');
      }

      handleCloseModal();
      fetchFields();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save custom field:', err);
      setError('Failed to save custom field');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      await contactsService.deleteCustomField(deleteConfirm.id);
      setSuccess('Custom field deleted successfully');
      setDeleteConfirm(null);
      fetchFields();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to delete custom field:', err);
      setError('Failed to delete custom field');
    } finally {
      setDeleting(false);
    }
  };

  // Sort fields by sortOrder for display
  const sortedFields = [...fields].sort((a, b) => a.sortOrder - b.sortOrder);

  // Handle drag end - reorder fields
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sortedFields.findIndex((f) => f.id === active.id);
    const newIndex = sortedFields.findIndex((f) => f.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistically update the UI
    const reorderedFields = arrayMove(sortedFields, oldIndex, newIndex);
    
    // Update sort orders for all affected fields
    const updatedFields = reorderedFields.map((field, index) => ({
      ...field,
      sortOrder: index,
    }));
    
    setFields(updatedFields);

    // Persist to backend
    try {
      setError(null);
      
      // Update all fields that changed position
      const updatePromises = updatedFields
        .filter((field, index) => {
          const originalField = sortedFields.find((f) => f.id === field.id);
          return originalField && originalField.sortOrder !== index;
        })
        .map((field) => 
          contactsService.updateCustomField(field.id, { sortOrder: field.sortOrder })
        );

      await Promise.all(updatePromises);
    } catch (err) {
      console.error('Failed to reorder fields:', err);
      setError('Failed to save new order. Please try again.');
      // Revert to original order
      fetchFields();
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-heading font-semibold text-white">Custom Fields</h2>
          <p className="text-sm text-(--color-muted-gray) mt-1">
            Define custom fields to collect additional information about your contacts
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="shrink-0">
          <Plus className="w-4 h-4 mr-2" />
          Add Field
        </Button>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
          >
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30"
          >
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
            <p className="text-sm text-green-300 flex-1">{success}</p>
            <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-300">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
            <span className="text-(--color-muted-gray)">Loading custom fields...</span>
          </div>
        </Card>
      ) : fields.length === 0 ? (
        /* Empty State */
        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center mx-auto mb-4">
              <Settings2 className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No Custom Fields Yet</h3>
            <p className="text-sm text-(--color-muted-gray) mb-6 max-w-md mx-auto">
              Create custom fields to collect additional information like company size, industry, budget, or any other data specific to your business.
            </p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Field
            </Button>
          </div>
        </Card>
      ) : (
        /* Fields List with Drag & Drop */
        <Card className="overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedFields.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="divide-y divide-(--color-border-gray)">
                {sortedFields.map((field, index) => (
                  <SortableFieldItem
                    key={field.id}
                    field={field}
                    index={index}
                    onEdit={() => handleOpenModal(field)}
                    onDelete={() => setDeleteConfirm(field)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingField ? 'Edit Custom Field' : 'Add Custom Field'}
        subtitle={editingField ? 'Update field settings' : 'Create a new custom field for contacts'}
        showCloseButton
      >
        <div className="space-y-5 p-6">
          {/* Field Label */}
          <Input
            label="Field Label"
            placeholder="e.g., Company Size"
            value={formData.label}
            onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
            required
            hint="This is what users will see when filling out the field"
          />

          {/* Field Name (Auto-generated from label or manual) */}
          <Input
            label="Field Name (API key)"
            placeholder="e.g., company_size"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            required
            hint="Used internally, should be lowercase with underscores"
          />

          {/* Field Type */}
          <Select
            label="Field Type"
            options={FIELD_TYPES}
            value={formData.type}
            onChange={(value) => setFormData((prev) => ({ ...prev, type: value as string }))}
            required
          />

          {/* Options for SELECT/MULTI_SELECT */}
          {(formData.type === 'SELECT' || formData.type === 'MULTI_SELECT') && (
            <div>
              <label className="block text-sm font-medium text-(--color-soft-gray) mb-2">
                Options <span className="text-(--color-error)">*</span>
              </label>
              
              {/* Add Option Input */}
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Add an option..."
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  className="flex-1"
                />
                <Button type="button" onClick={handleAddOption} variant="secondary">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Options List */}
              {formData.options.length > 0 ? (
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/10"
                    >
                      <span className="text-sm text-gray-300">{option}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(option)}
                        className="text-gray-500 hover:text-red-400 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-(--color-muted-gray) text-center py-3 border border-dashed border-(--color-border-gray) rounded-lg">
                  No options added yet
                </p>
              )}
            </div>
          )}

          {/* Required Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
            <div>
              <p className="text-sm font-medium text-white">Required Field</p>
              <p className="text-xs text-(--color-muted-gray) mt-0.5">
                Users must fill this field when adding a contact
              </p>
            </div>
            <Checkbox
              checked={formData.isRequired}
              onChange={(e) => setFormData((prev) => ({ ...prev, isRequired: e.target.checked }))}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-(--color-border-gray)">
          <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {editingField ? 'Update Field' : 'Create Field'}
              </>
            )}
          </Button>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Custom Field"
        description={`Are you sure you want to delete the "${deleteConfirm?.label}" field? This will remove all data stored in this field from your contacts. This action cannot be undone.`}
        confirmText="Delete Field"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default CustomFieldsPage;

